import { OperationalRoutes } from '@shared/navigation/OperationalRoutes';
import { lazy, ReactNode } from 'react';
import { BrowserRouter, Route } from 'react-router-dom';

// Importar componentes actualizados
import NavigationLayout from '@shared/components/navigation/NavigationLayout';
import { ImpersonationBanner } from '@shared/components/ui/ImpersonationBanner';
import { Page404 } from '@shared/components/ui/NotFound';
import { OperatorInactivityProvider } from '@shared/providers/OperatorInactivityProvider';
import GuestGuard from './GuestGuard';
import { guestRoutes } from './guestRoutes';
import PermissionRoute from './PermissionRoute';
import ProtectedRoute from './ProtectedRoute';
import { ProtectedRouteChildConfig, ProtectedRouteConfig, protectedRoutes } from './protectedRoutes';

// Landing pública para la revisión de Amazon (Public Solution Provider).
// Diferida (FE-ARCH-008): fuera de la app logueada, no debe viajar en el chunk
// de arranque. La absorbe el <Suspense> raíz de App.tsx.
const AmazonLandingPage = lazy(() => import('@modules/AmazonLanding/pages/AmazonLandingPage'));

export interface RouteConfig {
  path: string;
  element: ReactNode;
}

function wrapPermission(route: ProtectedRouteConfig, children: ReactNode) {
  return (
    <PermissionRoute
      routePath={route.path}
      teamTypes={route.teamTypes}
      permissions={route.permissions}
      requireAll={route.requireAll}
      requireRoleAdmin={route.requireRoleAdmin}
      roleAdminAlternativeAccess={route.roleAdminAlternativeAccess}
      requireWalletVisible={route.requireWalletVisible}
      requireCRMEnabled={route.requireCRMEnabled}
      requireProfitabilityAccess={route.requireProfitabilityAccess}
    >
      {children}
    </PermissionRoute>
  );
}

function renderProtectedRoute(route: ProtectedRouteConfig, index: number) {
  const key = `protected-${index}`;

  if (route.children?.length) {
    return (
      <Route key={key} path={route.path} element={wrapPermission(route, route.element)}>
        {route.children.map((child: ProtectedRouteChildConfig, childIndex: number) =>
          child.index ? (
            <Route key={`${key}-index-${childIndex}`} index element={child.element} />
          ) : (
            <Route key={`${key}-child-${childIndex}`} path={child.path} element={child.element} />
          ),
        )}
      </Route>
    );
  }

  return <Route key={key} path={route.path} element={wrapPermission(route, route.element)} />;
}

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <ImpersonationBanner />
      <OperatorInactivityProvider />
      <OperationalRoutes>
        {/* Landing pública: sin guard y sin layout. Un visitante anónimo debe
            verla sin que nada le redirija a /login, y un usuario con sesión
            tampoco puede ser expulsado de ella. */}
        <Route path="/amazon" element={<AmazonLandingPage />} />

        {/* Public/Guest Routes */}
        <Route element={<GuestGuard />}>
          {guestRoutes.map((route, index) => (
            <Route key={`guest-${index}`} path={route.path} element={route.element} />
          ))}
        </Route>

        {/* Protected Routes with New Navigation */}
        <Route path="/" element={<ProtectedRoute />}>
          <Route path="/" element={<NavigationLayout />}>
            {protectedRoutes.map((route, index) => renderProtectedRoute(route, index))}
          </Route>
        </Route>

        {/* Ruta catch-all para páginas no encontradas */}
        <Route path="*" element={<Page404 />} />
      </OperationalRoutes>
    </BrowserRouter>
  );
};

export default AppRoutes;
